/**
 * UserSearchModal
 * Dialog to search for users and start a new DM conversation.
 * Uses the existing GET /api/users/search endpoint.
 */

import axios from "axios"
import { Search } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { Avatar } from "@/components/ui/avatar"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import { apiClient } from "@/lib/api-client"
import { useCreateDmConversation } from "../../hooks/useDmChat"

interface SearchUser {
  id: string
  name: string
  username: string
  profilePhoto: string | null
}

interface UserSearchModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function UserSearchModal({ open, onOpenChange }: UserSearchModalProps) {
  const { t } = useTranslation("chats")
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchUser[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const navigate = useNavigate()
  const createConversation = useCreateDmConversation()
  const currentUserId = useAuthStore(s => s.user?.id)

  const handleSearch = async (searchQuery: string) => {
    setQuery(searchQuery)
    if (searchQuery.trim().length < 2) {
      setResults([])
      return
    }

    setIsSearching(true)
    try {
      const response = await apiClient.get<{
        success: boolean
        data: { users: SearchUser[] }
      }>("/api/users/search", {
        params: { search: searchQuery.trim(), limit: 10 },
      })
      const users = response.data.data.users || []
      setResults(currentUserId ? users.filter(u => u.id !== currentUserId) : users)
    } catch {
      setResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleSelectUser = async (user: SearchUser) => {
    try {
      const conversation = await createConversation.mutateAsync(user.id)
      onOpenChange(false)
      setQuery("")
      setResults([])
      navigate(`/chats/dm/${conversation.id}`)
    } catch (error) {
      const message =
        axios.isAxiosError(error) &&
        typeof error.response?.data === "object" &&
        error.response?.data !== null &&
        "message" in error.response.data
          ? String((error.response.data as { message: unknown }).message)
          : t("common.toasts.failedToCreate")
      toast.error(message)
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setQuery("")
      setResults([])
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("dmSearch.title")}</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={e => handleSearch(e.target.value)}
            placeholder={t("dmSearch.placeholder")}
            className="pl-10"
            autoFocus
          />
        </div>

        <div className="max-h-64 overflow-y-auto -mx-6 px-6">
          {isSearching && (
            <div className="flex items-center justify-center py-6">
              <div className="size-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!isSearching && query.trim().length >= 2 && results.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-6">
              {t("common.noUsersFound")}
            </p>
          )}

          {results.map(user => (
            <button
              type="button"
              key={user.id}
              onClick={() => handleSelectUser(user)}
              disabled={createConversation.isPending}
              className="w-full flex items-center gap-3 px-2 py-2.5 rounded-md hover:bg-accent transition-colors disabled:opacity-50"
            >
              <Avatar
                src={user.profilePhoto || undefined}
                alt={user.name}
                fallback={user.name}
                size="md"
              />
              <div className="text-left min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">@{user.username}</p>
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
